import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/authContext";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { FaUser } from "react-icons/fa";
import { FaSpinner } from "react-icons/fa"; // Import the spinner icon
import Header from "./Header";

function Profile() {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => setIsOpen(!isOpen);

  const { currentUser } = useAuth(); // Correctly destructure `currentUser`
  const [formData, setFormData] = useState({
    displayName: "",
    phoneNumber: "",
    email: "",
    profilePic: "",
  });
  
  // Separate loading states for each button
  const [loadingUpdate, setLoadingUpdate] = useState(false); // Loading for update
  const [loadingReset, setLoadingReset] = useState(false);   // Loading for reset
  const [upgrading, setUpgrading] = useState(false);

  // Populate the form when `currentUser` changes
  useEffect(() => {
    if (currentUser) {
      setFormData({
        displayName: currentUser.displayName || "",
        phoneNumber: currentUser.phoneNumber || "",
        email: currentUser.email || "",
        profilePic: currentUser.photoURL || "",
      });
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoadingUpdate(true); // Set loading state to true for update button
    try {
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);

        // Update user document in Firestore
        await updateDoc(userDocRef, {
          displayName: formData.displayName,
          phoneNumber: formData.phoneNumber,
          profilePic: formData.profilePic,
        });

        alert("Profile updated successfully!");
      } else {
        alert("User not logged in.");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("An error occurred while updating the profile.");
    } finally {
      setLoadingUpdate(false); // Reset loading state when done
    }
  };

  const handleReset = async () => {
    setLoadingReset(true); // Start loading spinner
    try {
      if (currentUser) {
        // Reset to original data from Firebase Authentication
        const originalData = {
          displayName: currentUser.displayName || "",
          phoneNumber: currentUser.phoneNumber || "",
          email: currentUser.email || "",
          profilePic: currentUser.photoURL || "",
        };
  
        setFormData(originalData); // Update the form data
  
        // Optionally update Firestore to reflect these values
        const userDocRef = doc(db, "users", currentUser.uid);
        await updateDoc(userDocRef, originalData);
  
        alert("Profile reset to original signup data.");
      } else {
        alert("User not logged in.");
      }
    } catch (error) {
      console.error("Error resetting profile:", error);
      alert("An error occurred while resetting the profile.");
    } finally {
      setLoadingReset(false); // Set loading state to false after reset completes
    }
  };

  const handleUpgrade = async () => {
    if (!currentUser) return;
    const confirmed = window.confirm(`Are you sure you want to upgrade your account to Reseller? This will deduct the one-time upgrade fee from your wallet.`);
    if (!confirmed) return;
    
    setUpgrading(true);
    try {
        const idToken = await currentUser.getIdToken();
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const res = await fetch(`${apiUrl}/api/v1/users/upgrade`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${idToken}` }
        });
        if (res.ok) {
            alert("Account upgraded successfully! You are now a Reseller.");
            window.location.reload();
        } else {
            const errData = await res.json().catch(() => ({}));
            alert(`Upgrade failed: ${errData.detail || 'Unknown error'}`);
        }
    } catch (err) {
        console.error(err);
        alert("An error occurred during upgrade.");
    } finally {
        setUpgrading(false);
    }
  };

  return (
    <div className="header-div h-screen bg-light1 text-dark2 dark:text-light1 dark:bg-dark1">
      <Header onToggleMenuClick={toggleMenu} isOpen={isOpen} />
      <div className="flex pt-16 w-9/10 mx-auto">
        <main
          className={`${
            isOpen ? "lg:ml-40" : "lg:ml-11"
          } transition-all duration-300 space-y-8 ease-in-out w-screen`}
        >
          <div className="flex items-center rounded-lg bg-light2 dark:bg-dark2 p-2">
            <div className="flex items-center mr-auto">
              <div className="flex items-center justify-center mx-2 w-10 h-10 rounded-full bg-light1 dark:bg-dark1">
                <FaUser />
              </div>
              <span>{formData.displayName}</span>
            </div>
            <div className="flex gap-2">
              {(currentUser?.role === 'user' || currentUser?.role === 'regular') && (
                  <button onClick={handleUpgrade} disabled={upgrading} className="btn bg-yellow-500 hover:bg-yellow-600 text-white font-medium border-0">
                      {upgrading ? 'Upgrading...' : 'Upgrade to Reseller'}
                  </button>
              )}
              <button
                className="btn"
                onClick={handleReset}
                disabled={loadingReset} // Disable button when loadingReset is true
              >
                {loadingReset ? (
                  <FaSpinner className="animate-spin" /> // Show spinner when loadingReset is true
                ) : (
                  "Reset Profile"
                )}
              </button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row justify-between p-8 md:px-16 lg:p-16 rounded-lg border dark:border-dark2 p-2">
            <div className="flex justify-center items-center">
              {formData.profilePic && (
                <img
                  src={formData.profilePic}
                  alt="Profile"
                  className="w-48 h-48 rounded-full m-2"
                />
              )}
            </div>
            <form
              className="flex flex-col space-y-4 w-full lg:w-7/10 m-2"
              onSubmit={handleUpdate}
            >
              <div className="flex flex-col lg:flex-row lg:space-x-2">
                <label className="text-dark0 mr-auto">Display Name</label>
                <input
                  className="lg:w-7/10 bg-light2 dark:bg-dark2 rounded-md p-1"
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col lg:flex-row lg:space-x-2">
                <label className="text-dark0 mr-auto">Phone Number</label>
                <input
                  className="lg:w-7/10 bg-light2 dark:bg-dark2 rounded-md p-1"
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col lg:flex-row lg:space-x-2">
                <label className="text-dark0 mr-auto">Email</label>
                <input
                  className="lg:w-7/10 bg-light2 dark:bg-dark2 rounded-md p-1"
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled // Email shouldn't be updated here
                />
              </div>
              <div className="flex flex-col lg:flex-row lg:space-x-2">
                <label className="text-dark0 mr-auto">Profile Picture URL</label>
                <input
                  className="lg:w-7/10 bg-light2 dark:bg-dark2 rounded-md p-1"
                  type="text"
                  name="profilePic"
                  value={formData.profilePic}
                  onChange={handleChange}
                />
              </div>
              <button
                type="submit"
                className="btn"
                disabled={loadingUpdate} // Disable button when loadingUpdate is true
              >
                {loadingUpdate ? (
                  <span className="flex justify-center items-center">
                    <FaSpinner className="animate-spin mx-4" /> Updating...
                  </span> // Show spinner when loadingUpdate is true
                ) : (
                  "Update Profile"
                )}
              </button>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}

export default Profile;











// import React, { useState, useEffect } from "react";
// import { useAuth } from "../../context/authContext";
// import { doc, updateDoc } from "firebase/firestore";
// import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
// import { db, storage } from "../../firebase/firebaseConfig"; // Ensure you import the `storage` instance
// import { FaUser } from "react-icons/fa";
// import { FaSpinner } from "react-icons/fa";
// import Header from "./Header";

// function Profile() {
//   const [isOpen, setIsOpen] = useState(false);
//   const toggleMenu = () => setIsOpen(!isOpen);

//   const { currentUser } = useAuth();
//   const [formData, setFormData] = useState({
//     displayName: "",
//     phoneNumber: "",
//     email: "",
//     profilePic: "",
//   });

//   const [loadingUpdate, setLoadingUpdate] = useState(false);
//   const [loadingReset, setLoadingReset] = useState(false);
//   const [uploading, setUploading] = useState(false); // Loading state for image upload

//   useEffect(() => {
//     if (currentUser) {
//       setFormData({
//         displayName: currentUser.displayName || "",
//         phoneNumber: currentUser.phoneNumber || "",
//         email: currentUser.email || "",
//         profilePic: currentUser.photoURL || "",
//       });
//     }
//   }, [currentUser]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//   };

//   const handleUpdate = async (e) => {
//     e.preventDefault();
//     setLoadingUpdate(true);
//     try {
//       if (currentUser) {
//         const userDocRef = doc(db, "users", currentUser.uid);

//         await updateDoc(userDocRef, {
//           displayName: formData.displayName,
//           phoneNumber: formData.phoneNumber,
//           profilePic: formData.profilePic,
//         });

//         alert("Profile updated successfully!");
//       } else {
//         alert("User not logged in.");
//       }
//     } catch (error) {
//       console.error("Error updating profile:", error);
//       alert("An error occurred while updating the profile.");
//     } finally {
//       setLoadingUpdate(false);
//     }
//   };

//   const handleReset = async () => {
//     setLoadingReset(true);
//     try {
//       if (currentUser) {
//         const originalData = {
//           displayName: currentUser.displayName || "",
//           phoneNumber: currentUser.phoneNumber || "",
//           email: currentUser.email || "",
//           profilePic: currentUser.photoURL || "",
//         };

//         setFormData(originalData);

//         const userDocRef = doc(db, "users", currentUser.uid);
//         await updateDoc(userDocRef, originalData);

//         alert("Profile reset to original signup data.");
//       } else {
//         alert("User not logged in.");
//       }
//     } catch (error) {
//       console.error("Error resetting profile:", error);
//       alert("An error occurred while resetting the profile.");
//     } finally {
//       setLoadingReset(false);
//     }
//   };

//   const handleFileChange = (e) => {
//     const file = e.target.files[0];
//     if (!file) return;

//     const storageRef = ref(storage, `profilePics/${currentUser.uid}-${file.name}`);
//     const uploadTask = uploadBytesResumable(storageRef, file);

//     setUploading(true);

//     uploadTask.on(
//       "state_changed",
//       null,
//       (error) => {
//         console.error("Error uploading file:", error);
//         setUploading(false);
//       },
//       async () => {
//         const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
//         setFormData((prev) => ({ ...prev, profilePic: downloadURL }));
//         setUploading(false);
//         alert("Profile picture uploaded successfully!");
//       }
//     );
//   };

//   return (
//     <div className="header-div h-screen bg-light1 text-dark2 dark:text-light1 dark:bg-dark1">
//       <Header onToggleMenuClick={toggleMenu} isOpen={isOpen} />
//       <div className="flex pt-16 w-9/10 mx-auto">
//         <main
//           className={`${
//             isOpen ? "lg:ml-40" : "lg:ml-11"
//           } transition-all duration-300 space-y-8 ease-in-out w-screen`}
//         >
//           <div className="flex items-center rounded-lg bg-light2 dark:bg-dark2 p-2">
//             <div className="flex items-center mr-auto">
//               <div className="flex items-center justify-center mx-2 w-10 h-10 rounded-full bg-light1 dark:bg-dark1">
//                 <FaUser />
//               </div>
//               <span>{formData.displayName}</span>
//             </div>
//             <button
//               className="btn"
//               onClick={handleReset}
//               disabled={loadingReset}
//             >
//               {loadingReset ? <FaSpinner className="animate-spin" /> : "Reset Profile"}
//             </button>
//           </div>

//           <div className="flex flex-col lg:flex-row justify-between p-8 md:px-16 lg:p-16 rounded-lg border dark:border-dark2 p-2">
//             <div className="flex flex-col justify-center items-center">
//               {formData.profilePic && (
//                 <img
//                   src={formData.profilePic}
//                   alt="Profile"
//                   className="w-48 h-48 rounded-full m-2"
//                 />
//               )}
//               <input
//                 type="file"
//                 accept="image/*"
//                 onChange={handleFileChange}
//                 disabled={uploading}
//                 className="btn mt-2"
//               />
//               {uploading && <p>Uploading...</p>}
//             </div>
//             <form
//               className="flex flex-col space-y-4 w-full lg:w-7/10 m-2"
//               onSubmit={handleUpdate}
//             >
//               <div className="flex flex-col lg:flex-row lg:space-x-2">
//                 <label className="text-dark0 mr-auto">Display Name</label>
//                 <input
//                   className="lg:w-7/10 bg-light2 dark:bg-dark2 rounded-md p-1"
//                   type="text"
//                   name="displayName"
//                   value={formData.displayName}
//                   onChange={handleChange}
//                 />
//               </div>
//               <div className="flex flex-col lg:flex-row lg:space-x-2">
//                 <label className="text-dark0 mr-auto">Phone Number</label>
//                 <input
//                   className="lg:w-7/10 bg-light2 dark:bg-dark2 rounded-md p-1"
//                   type="text"
//                   name="phoneNumber"
//                   value={formData.phoneNumber}
//                   onChange={handleChange}
//                 />
//               </div>
//               <div className="flex flex-col lg:flex-row lg:space-x-2">
//                 <label className="text-dark0 mr-auto">Email</label>
//                 <input
//                   className="lg:w-7/10 bg-light2 dark:bg-dark2 rounded-md p-1"
//                   type="text"
//                   name="email"
//                   value={formData.email}
//                   disabled
//                 />
//               </div>
//               <button
//                 type="submit"
//                 className="btn"
//                 disabled={loadingUpdate}
//               >
//                 {loadingUpdate ? (
//                   <span className="flex justify-center items-center">
//                     <FaSpinner className="animate-spin mx-4" /> Updating...
//                   </span>
//                 ) : (
//                   "Update Profile"
//                 )}
//               </button>
//             </form>
//           </div>
//         </main>
//       </div>
//     </div>
//   );
// }

// export default Profile;
